import { useCallback, useState } from 'react'

import { useLocation, useNavigate } from 'Pods/Shared/ContextBasedRouter'
import { parseRunSlugFromPathname } from 'Pods/ThreeDStudio/Hooks/parseRunSlugFromPathname'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import { threeDStudioStore } from 'Pods/ThreeDStudio/Store'
import { clearModelSettings, setInputThreeD } from 'Pods/ThreeDStudio/Store/actions/modelSettings'
import {
  setAcquiredSlug,
  setActiveMobilePanel,
  setIsModelSettingsOpen,
  setIsSidebarOpen,
  setIsViewerSidebarOpen,
} from 'Pods/ThreeDStudio/Store/actions/ui'
import { MOBILE_PANEL } from 'Pods/ThreeDStudio/Store/constants'

import useBreakpoint from 'Utils/useBreakpoint'

import { prepareAcquiredModelAsset } from '../Api/prepareAcquiredModelAsset'

// Keep in sync with the 0.3s width transition in _viewer_sidebar.scss. Parsing the
// model blocks the main thread, so loading must wait until the sidebar open animation
// has finished or the animation janks.
const SIDEBAR_ANIMATION_MS = 300
// Breakpoints where the workspace collapses into tabs - keep in sync with the
// media-breakpoint-down(md) rule on .mobile-tab-bar.
const MOBILE_BREAKPOINTS = ['xs', 'sm', 'md']

const waitForSidebarAnimation = () =>
  new Promise((resolve) => {
    setTimeout(resolve, SIDEBAR_ANIMATION_MS)
  })

/**
 * Opens an acquired model in the viewer: prepares the generator asset, then follows the
 * same path an upload takes (see useThreeDModelUploader) so a picked model and an
 * uploaded one land in exactly the same state - viewer only, no library card.
 */
export default function useAcquiredModelOpener() {
  const t = useTranslation()
  const [isOpening, setIsOpening] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const breakpoint = useBreakpoint()
  const isMobileLayout = MOBILE_BREAKPOINTS.includes(breakpoint)

  const openModel = useCallback(async (modelId, { targetPathname = '/ai' } = {}) => {
    const wasViewerSidebarOpen = threeDStudioStore.getState().ui?.isViewerSidebarOpen
    // Must run before the navigation, so useModelUrlSync knows the incoming slug is a
    // purchase by the time the pathname change reaches it.
    setAcquiredSlug(parseRunSlugFromPathname(targetPathname))
    if (location?.pathname !== targetPathname) {
      navigate(targetPathname)
      clearModelSettings()
      // clearModelSettings re-opens the model settings panel for fresh-generation flows;
      // opening a purchase only shows the viewer, so keep it collapsed.
      setIsModelSettingsOpen(false)
      // clearModelSettings also force-closes the viewer sidebar. Reopen it in the same
      // synchronous tick when it was already open so it never visibly closes-then-reopens.
      if (wasViewerSidebarOpen) {
        setIsViewerSidebarOpen(true)
      }
    }
    if (isMobileLayout) {
      setIsSidebarOpen(false)
      setActiveMobilePanel(MOBILE_PANEL.VIEWER)
    }
    setIsOpening(true)

    try {
      const { assetId, url, fileSize, format } = await prepareAcquiredModelAsset(modelId)
      const wasSidebarOpen = threeDStudioStore.getState().ui?.isViewerSidebarOpen
      setIsViewerSidebarOpen(true)
      if (!wasSidebarOpen) {
        await waitForSidebarAnimation()
      }
      setInputThreeD(assetId, url, fileSize, format)
      return true
    } catch {
      window.showNotification(t('threeDModelPicker.openError'), { type: 'error', autohide: true })
      return false
    } finally {
      setIsOpening(false)
    }
  }, [location?.pathname, navigate, isMobileLayout, t])

  return { isOpening, openModel }
}
